import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, throwError, of, BehaviorSubject } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

interface Tag {
  id: number;
  value: string;
  color: string;
}

@Injectable({
  providedIn: 'root',
})
export class TagsService {
  private apiBaseUri = `${environment.apiUri}/tags`;
  private availableTagsCache$: { [publicId: string]: Observable<Tag[]> } = {};
  private availableTagsSubjects: { [publicId: string]: BehaviorSubject<Tag[]> } = {};

  constructor(private http: HttpClient) {}

  // Method to unmark a tag from a connection
  unmarkTag(publicId: string): Observable<any> {
    const url = `${this.apiBaseUri}/unmark/${publicId}`;
    return this.http.post(url, { withCredentials: true }).pipe(
      catchError(this.handleError)
    );
  }

  markTag(publicId: string, tagId: number): Observable<any> {
    const url = `${this.apiBaseUri}/mark/${publicId}`;
    return this.http.post(
      url,
      { tag: tagId },  // Make sure the tag parameter is correctly set
      { withCredentials: true }
    ).pipe(
      catchError(this.handleError)
    );
  }
  
  // Method to get available tags for a specific connection
  getAvailableTags(publicId: string, forceRefresh: boolean = false): Observable<Tag[]> {
    if (forceRefresh) {
      this.clearCache(publicId);
    }
  
    if (!this.availableTagsCache$[publicId]) {
      this.availableTagsCache$[publicId] = this.http.get<{ data: Tag[] }>(
        `${this.apiBaseUri}/available/${publicId}`, { withCredentials: true }
      ).pipe(
        map(response => response.data),
        shareReplay(1),
        catchError((error) => {
          console.error('Error fetching available tags:', error);
          return of([]);
        })
      );
  
      if (!this.availableTagsSubjects[publicId]) {
        this.availableTagsSubjects[publicId] = new BehaviorSubject<Tag[]>([]);
      }
  
      this.availableTagsCache$[publicId].subscribe((tags) => {
        this.availableTagsSubjects[publicId].next(tags);
      });
    }
  
    return this.availableTagsSubjects[publicId].asObservable();
  }
  

  // Common error handling for all requests
  private handleError(error: any) {
    console.error('API Error:', error);
    return throwError(() => 'Something went wrong; please try again later.');
  }

  // Clear the cache for the available tags of a specific connection
  clearCache(publicId: string): void {
    delete this.availableTagsCache$[publicId];
    if (this.availableTagsSubjects[publicId]) {
      this.availableTagsSubjects[publicId].next([]);
    }
  }
}
